import React, { useCallback, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import axios from 'axios';
import toast from 'react-hot-toast';
import { z } from 'zod';
import {
  type Academy,
  type Lesson,
  type Term,
  type Region,
  parseLocalDateYmd,
  starterRequestRequiresGuardian,
} from '@court-sync/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  PhoneField,
  Textarea,
} from '@/components/ui';
import { useFormatDate } from '@/hooks/useFormatDate';
import { formatTimeHHMM } from '@/utils/timeFormat';
import { starterRequestBookingService } from '@/services/starterRequestBooking';
import { useAcademyPrimaryColor } from '@/contexts/AcademyBrandingContext';
import { CheckCircle2 } from 'lucide-react';

const genderSchema = z.enum(['male', 'female', 'other']);
const skillLevelSchema = z.enum(['beginner', 'intermediate', 'advanced']);

const optionalPhoneFieldSchema = z.string().optional();

const guardianFormSchema = z.object({
  name: z.string().trim().max(255).optional(),
  relationship: z.string().trim().max(100).optional(),
  email: z.string().trim().max(255).optional(),
  phone: optionalPhoneFieldSchema,
});

const starterRequestFormSchema = z
  .object({
    player: z.object({
      firstName: z.string().trim().min(1, 'First name is required').max(100),
      lastName: z.string().trim().min(1, 'Last name is required').max(100),
      dateOfBirth: z
        .string()
        .trim()
        .min(1, 'Date of birth is required')
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
      gender: genderSchema,
      skillLevel: skillLevelSchema.optional(),
      medicalNotes: z.string().trim().max(2000).optional(),
    }),
    contact: z.object({
      email: z.string().trim().email('Enter a valid email').max(255),
      phone: optionalPhoneFieldSchema,
    }),
    guardian: guardianFormSchema.optional(),
    guardianIsContact: z.boolean().optional(),
    campBookingType: z.enum(['daily', 'full_camp']).optional(),
    campBookedDays: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).optional(),
    note: z.string().trim().max(1000).optional(),
  })
  .superRefine((data, ctx) => {
    const needsGuardian = starterRequestRequiresGuardian(data.player.dateOfBirth);
    const guardianIsContact = data.guardianIsContact !== false;

    if (needsGuardian) {
      if (!data.guardian?.name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Guardian name is required for players under 18',
          path: ['guardian', 'name'],
        });
      }
      if (!data.guardian?.relationship?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Relationship is required',
          path: ['guardian', 'relationship'],
        });
      }
      if (guardianIsContact) {
        if (!data.contact.email?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Email is required',
            path: ['contact', 'email'],
          });
        }
      } else if (!data.guardian?.email?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Guardian email is required',
          path: ['guardian', 'email'],
        });
      } else if (!z.string().email().safeParse(data.guardian.email.trim()).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter a valid email',
          path: ['guardian', 'email'],
        });
      }
    }

    if (data.campBookingType === 'daily' && !data.campBookedDays?.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Select at least one camp day',
        path: ['campBookedDays'],
      });
    }
  });

type StarterRequestFormValues = z.infer<typeof starterRequestFormSchema>;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function resolveDefaultCampBookingType(lesson: Lesson): 'daily' | 'full_camp' | undefined {
  if (lesson.lessonType !== 'camp') return undefined;
  const hasDay = lesson.campDayPrice != null;
  const hasFull = lesson.campFullPrice != null;
  if (hasDay && !hasFull) return 'daily';
  if (hasFull) return 'full_camp';
  if (hasDay) return 'daily';
  return 'full_camp';
}

function resolveUpcomingCampDays(lesson: Lesson): string[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (lesson.campSessionDates?.length) {
    return lesson.campSessionDates
      .map((d) => parseLocalDateYmd(d))
      .filter((d): d is Date => d !== null && d >= today)
      .sort((a, b) => a.getTime() - b.getTime())
      .map((d) => format(d, 'yyyy-MM-dd'));
  }

  if (!lesson.startDate || !lesson.endDate) return [];

  const start = parseLocalDateYmd(
    lesson.startDate instanceof Date
      ? format(lesson.startDate, 'yyyy-MM-dd')
      : String(lesson.startDate).slice(0, 10)
  );
  const end = parseLocalDateYmd(
    lesson.endDate instanceof Date
      ? format(lesson.endDate, 'yyyy-MM-dd')
      : String(lesson.endDate).slice(0, 10)
  );
  if (!start || !end) return [];

  const days: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    if (cursor >= today) {
      days.push(format(cursor, 'yyyy-MM-dd'));
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

interface CampDayPickerProps {
  lesson: Lesson;
  selectedDays: string[];
  onChange: (days: string[]) => void;
  formatDate: ReturnType<typeof useFormatDate>['formatDate'];
  region?: Region | null;
  error?: string;
}

function CampDayPicker({
  lesson,
  selectedDays,
  onChange,
  formatDate,
  region,
  error,
}: CampDayPickerProps) {
  const primaryColor = useAcademyPrimaryColor();
  const availableDays = useMemo(() => resolveUpcomingCampDays(lesson), [lesson]);
  const selectedSet = useMemo(() => new Set(selectedDays), [selectedDays]);

  const toggleDay = (day: string) => {
    const next = new Set(selectedSet);
    if (next.has(day)) next.delete(day);
    else next.add(day);
    onChange([...next].sort());
  };

  if (availableDays.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No upcoming camp days are listed for this camp.</p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid gap-2 max-h-48 overflow-y-auto">
        {availableDays.map((day) => {
          const date = parseLocalDateYmd(day);
          const isChecked = selectedSet.has(day);
          return (
            <button
              key={day}
              type="button"
              onClick={() => toggleDay(day)}
              className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                isChecked ? '' : 'border-border hover:bg-accent'
              }`}
              style={
                isChecked
                  ? { borderColor: primaryColor, backgroundColor: `${primaryColor}0d` }
                  : undefined
              }
            >
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 border-muted-foreground"
                style={
                  isChecked
                    ? { backgroundColor: primaryColor, borderColor: primaryColor }
                    : undefined
                }
                aria-hidden
              >
                {isChecked ? (
                  <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                    <path
                      d="M10 3L5 8.5 2 5.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : null}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">
                  {date ? DAY_NAMES[date.getDay()] : day},{' '}
                  {date
                    ? formatDate(date, region, { month: 'long', day: 'numeric', year: 'numeric' })
                    : day}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatTimeHHMM(lesson.startTime)} – {formatTimeHHMM(lesson.endTime)}
                </p>
              </div>
            </button>
          );
        })}
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
}

export interface StarterRequestBookingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  academy: Academy;
  lesson: Lesson;
  term?: Term | null;
}

export function StarterRequestBookingModal({
  open,
  onOpenChange,
  academy,
  lesson,
  term,
}: StarterRequestBookingModalProps) {
  const { formatDate } = useFormatDate({ region: academy.region, publicContext: true });
  const [submitted, setSubmitted] = useState(false);

  const isCamp = lesson.lessonType === 'camp';
  const defaultCampType = resolveDefaultCampBookingType(lesson);
  const campOffersDaily = lesson.campDayPrice != null;
  const campOffersFull = lesson.campFullPrice != null;

  const initialFormValues = useMemo<StarterRequestFormValues>(
    () => ({
      player: {
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        gender: 'male',
        skillLevel: 'beginner',
        medicalNotes: '',
      },
      contact: { email: '', phone: '' },
      guardian: { name: '', relationship: '', email: '', phone: '' },
      guardianIsContact: true,
      campBookingType: defaultCampType,
      campBookedDays: [],
      note: '',
    }),
    [defaultCampType]
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<StarterRequestFormValues>({
    resolver: zodResolver(starterRequestFormSchema),
    defaultValues: initialFormValues,
  });

  const dateOfBirth = useWatch({ control, name: 'player.dateOfBirth' });
  const guardianIsContact = useWatch({ control, name: 'guardianIsContact' });
  const campBookingType = useWatch({ control, name: 'campBookingType' });
  const campBookedDays = useWatch({ control, name: 'campBookedDays' }) ?? [];
  const requiresGuardian = useMemo(() => {
    const dob = dateOfBirth?.trim() ?? '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) return false;
    return starterRequestRequiresGuardian(dob);
  }, [dateOfBirth]);

  const prepareForOpen = useCallback(() => {
    setSubmitted(false);
    reset(initialFormValues);
  }, [initialFormValues, reset]);

  const onSubmit = async (data: StarterRequestFormValues) => {
    const contactPhone =
      typeof data.contact.phone === 'string' && data.contact.phone.trim() === ''
        ? undefined
        : data.contact.phone;
    const guardianPhone =
      data.guardian &&
      typeof data.guardian.phone === 'string' &&
      data.guardian.phone.trim() === ''
        ? undefined
        : data.guardian?.phone;
    const mergedGuardianIsContact = data.guardianIsContact !== false;

    try {
      const res = await starterRequestBookingService.submit({
        academyId: academy.id,
        lessonId: lesson.id,
        termId: lesson.termId ?? term?.id ?? null,
        player: {
          ...data.player,
          skillLevel: data.player.skillLevel,
          medicalNotes: data.player.medicalNotes || undefined,
        },
        contact: {
          email: data.contact.email,
          phone: contactPhone,
        },
        guardian: requiresGuardian
          ? {
              name: data.guardian!.name,
              relationship: data.guardian!.relationship,
              email: mergedGuardianIsContact ? data.contact.email : data.guardian!.email,
              phone: mergedGuardianIsContact ? contactPhone : guardianPhone,
            }
          : undefined,
        note: data.note || undefined,
        ...(isCamp
          ? {
              campBookingType: data.campBookingType,
              campBookedDays:
                data.campBookingType === 'daily' ? data.campBookedDays : undefined,
            }
          : {}),
      });

      if (res.success) {
        setSubmitted(true);
        toast.success('Booking request submitted');
      } else {
        toast.error(res.error || 'Could not submit request');
      }
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) &&
        e.response?.data &&
        typeof e.response.data === 'object' &&
        'error' in e.response.data
          ? String((e.response.data as { error?: string }).error)
          : e instanceof Error
            ? e.message
            : 'Could not submit request';
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-lg overflow-y-auto"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          prepareForOpen();
        }}
      >
        {submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" aria-hidden />
                Request received
              </DialogTitle>
              <DialogDescription>
                {academy.name} will review your request and contact you at the email you provided.
                No payment is taken online on the Starter plan.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Request booking</DialogTitle>
              <DialogDescription>
                Submit your details for <span className="font-medium text-foreground">{lesson.name}</span>.
                The academy will confirm enrollment — no account or online payment required.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                <p className="text-sm font-medium text-foreground">Player</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field data-invalid={!!errors.player?.firstName}>
                    <FieldLabel htmlFor="sr-player-first">First name *</FieldLabel>
                    <Input id="sr-player-first" autoComplete="given-name" {...register('player.firstName')} />
                    <FieldError>{errors.player?.firstName?.message}</FieldError>
                  </Field>
                  <Field data-invalid={!!errors.player?.lastName}>
                    <FieldLabel htmlFor="sr-player-last">Last name *</FieldLabel>
                    <Input id="sr-player-last" autoComplete="family-name" {...register('player.lastName')} />
                    <FieldError>{errors.player?.lastName?.message}</FieldError>
                  </Field>
                </div>
                <Field data-invalid={!!errors.player?.dateOfBirth}>
                  <FieldLabel htmlFor="sr-player-dob">Date of birth *</FieldLabel>
                  <Input id="sr-player-dob" type="date" {...register('player.dateOfBirth')} />
                  <FieldError>{errors.player?.dateOfBirth?.message}</FieldError>
                </Field>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field data-invalid={!!errors.player?.gender}>
                    <FieldLabel htmlFor="sr-player-gender">Gender *</FieldLabel>
                    <select id="sr-player-gender" className="input w-full" {...register('player.gender')}>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    <FieldError>{errors.player?.gender?.message}</FieldError>
                  </Field>
                  <Field data-invalid={!!errors.player?.skillLevel}>
                    <FieldLabel htmlFor="sr-player-skill">Skill level</FieldLabel>
                    <select id="sr-player-skill" className="input w-full" {...register('player.skillLevel')}>
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                    <FieldError>{errors.player?.skillLevel?.message}</FieldError>
                  </Field>
                </div>
                <Field>
                  <FieldLabel htmlFor="sr-medical">Medical notes</FieldLabel>
                  <Textarea
                    id="sr-medical"
                    rows={2}
                    placeholder="Allergies, conditions, or other notes"
                    {...register('player.medicalNotes')}
                  />
                </Field>
              </div>

              {!requiresGuardian ? (
                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                  <p className="text-sm font-medium text-foreground">Your contact details</p>
                  <Field data-invalid={!!errors.contact?.email}>
                    <FieldLabel htmlFor="sr-contact-email">Email *</FieldLabel>
                    <Input
                      id="sr-contact-email"
                      type="email"
                      autoComplete="email"
                      {...register('contact.email')}
                    />
                    <FieldError>{errors.contact?.email?.message}</FieldError>
                  </Field>
                  <PhoneField
                    control={control}
                    name="contact.phone"
                    label="Phone (optional)"
                    id="sr-contact-phone"
                    purpose="guest_inquiry"
                  />
                </div>
              ) : guardianIsContact !== false ? (
                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                  <p className="text-sm font-medium text-foreground">Contact and parent/guardian</p>
                  <label className="flex items-start gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4"
                      {...register('guardianIsContact')}
                      defaultChecked
                    />
                    <span>The booking contact is the parent/guardian</span>
                  </label>
                  <Field data-invalid={!!errors.guardian?.name}>
                    <FieldLabel htmlFor="sr-guardian-name">Full name *</FieldLabel>
                    <Input id="sr-guardian-name" {...register('guardian.name')} />
                    <FieldError>{errors.guardian?.name?.message}</FieldError>
                  </Field>
                  <Field data-invalid={!!errors.guardian?.relationship}>
                    <FieldLabel htmlFor="sr-guardian-rel">Relationship *</FieldLabel>
                    <Input
                      id="sr-guardian-rel"
                      placeholder="e.g. Mother, Father, Guardian"
                      {...register('guardian.relationship')}
                    />
                    <FieldError>{errors.guardian?.relationship?.message}</FieldError>
                  </Field>
                  <Field data-invalid={!!errors.contact?.email}>
                    <FieldLabel htmlFor="sr-contact-email">Email *</FieldLabel>
                    <Input
                      id="sr-contact-email"
                      type="email"
                      autoComplete="email"
                      {...register('contact.email')}
                    />
                    <FieldError>{errors.contact?.email?.message}</FieldError>
                  </Field>
                  <PhoneField
                    control={control}
                    name="contact.phone"
                    label="Phone (optional)"
                    id="sr-contact-phone"
                    purpose="guest_inquiry"
                  />
                </div>
              ) : (
                <>
                  <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Your contact details</p>
                    <label className="flex items-start gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        {...register('guardianIsContact')}
                      />
                      <span>The booking contact is the parent/guardian</span>
                    </label>
                    <Field data-invalid={!!errors.contact?.email}>
                      <FieldLabel htmlFor="sr-contact-email">Email *</FieldLabel>
                      <Input
                        id="sr-contact-email"
                        type="email"
                        autoComplete="email"
                        {...register('contact.email')}
                      />
                      <FieldError>{errors.contact?.email?.message}</FieldError>
                    </Field>
                    <PhoneField
                      control={control}
                      name="contact.phone"
                      label="Phone (optional)"
                      id="sr-contact-phone"
                      purpose="guest_inquiry"
                    />
                  </div>
                  <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Parent / guardian</p>
                    <Field data-invalid={!!errors.guardian?.name}>
                      <FieldLabel htmlFor="sr-guardian-name">Full name *</FieldLabel>
                      <Input id="sr-guardian-name" {...register('guardian.name')} />
                      <FieldError>{errors.guardian?.name?.message}</FieldError>
                    </Field>
                    <Field data-invalid={!!errors.guardian?.relationship}>
                      <FieldLabel htmlFor="sr-guardian-rel">Relationship *</FieldLabel>
                      <Input
                        id="sr-guardian-rel"
                        placeholder="e.g. Mother, Father, Guardian"
                        {...register('guardian.relationship')}
                      />
                      <FieldError>{errors.guardian?.relationship?.message}</FieldError>
                    </Field>
                    <Field data-invalid={!!errors.guardian?.email}>
                      <FieldLabel htmlFor="sr-guardian-email-separate">Email *</FieldLabel>
                      <Input
                        id="sr-guardian-email-separate"
                        type="email"
                        {...register('guardian.email')}
                      />
                      <FieldError>{errors.guardian?.email?.message}</FieldError>
                    </Field>
                    <PhoneField
                      control={control}
                      name="guardian.phone"
                      label="Phone (optional)"
                      id="sr-guardian-phone"
                      purpose="guest_inquiry"
                    />
                  </div>
                </>
              )}

              {isCamp ? (
                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                  <p className="text-sm font-medium text-foreground">Camp booking</p>
                  {campOffersDaily && campOffersFull ? (
                    <fieldset className="space-y-2">
                      <legend className="text-sm text-muted-foreground">Booking type</legend>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          value="full_camp"
                          {...register('campBookingType')}
                        />
                        Full camp
                        {lesson.campFullPrice != null ? ` ($${lesson.campFullPrice})` : ''}
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          value="daily"
                          {...register('campBookingType')}
                        />
                        Selected days
                        {lesson.campDayPrice != null ? ` ($${lesson.campDayPrice}/day)` : ''}
                      </label>
                    </fieldset>
                  ) : null}

                  {campBookingType === 'daily' || (campOffersDaily && !campOffersFull) ? (
                    <div>
                      <p className="mb-2 text-sm text-muted-foreground">Select camp days *</p>
                      <CampDayPicker
                        lesson={lesson}
                        selectedDays={campBookedDays}
                        onChange={(days) =>
                          setValue('campBookedDays', days, { shouldValidate: true })
                        }
                        formatDate={formatDate}
                        region={academy.region}
                        error={errors.campBookedDays?.message}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              <Field>
                <FieldLabel htmlFor="sr-note">Note to academy</FieldLabel>
                <Textarea
                  id="sr-note"
                  rows={2}
                  placeholder="Optional message"
                  {...register('note')}
                />
              </Field>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting…' : 'Submit request'}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
